import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { useClubs } from "@/hooks/api/useClubs";
import type { Club } from "@/api/services/clubs.service";
import { cn } from "@/lib/utils";

interface Props {
  value: { name: string; logoUrl?: string | null } | null;
  onChange: (club: Club | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function ClubSelect({ value, onChange, placeholder = "Select club…", disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useClubs();
  const clubs = data?.clubs ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clubs;
    return clubs.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.shortName?.toLowerCase().includes(q) ||
        c.country?.toLowerCase().includes(q),
    );
  }, [clubs, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-left text-sm",
          "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {value?.logoUrl ? (
          <img src={value.logoUrl} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-gray-100 shrink-0" />
        )}
        <span className={cn("flex-1 truncate", !value?.name && "text-gray-400")}>
          {value?.name || placeholder}
        </span>
        {value?.name && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="p-1 rounded hover:bg-gray-100"
          >
            <X className="h-3 w-3 text-gray-400" />
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clubs…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-3 text-sm text-gray-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-sm text-gray-400">No clubs found</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange(c); setOpen(false); setQuery(""); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                >
                  {c.logoUrl ? (
                    <img src={c.logoUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-100" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{c.name}</p>
                    {c.country && <p className="text-xs text-gray-400 truncate">{c.country}</p>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
